/**
 * Pure functions of the `wordle` type: the progress blob, the submission, how a server
 * verdict folds back into the progress (ADR 0041, 0054) and what the keyboard knows of each
 * letter. No component here, so `module.ts` stays light in the eager bundle.
 */
import type { Feedback, Verdict } from "../types";
import {
  DEFAULT_LENGTH,
  lengthOf,
  type Guess,
  type Mark,
  type WordleDefinition,
  type WordleProgress,
  type WordleSubmission,
} from "./types";

const MARKS: readonly Mark[] = ["correct", "present", "absent"];

/** Mirrors the server's `normalize_word`: accents removed, uppercase, spaces trimmed. */
export function normalizeWord(word: string): string {
  return word
    .trim()
    .normalize("NFD")
    .replace(/\p{Mn}/gu, "")
    .normalize("NFC")
    .toUpperCase();
}

/** The one A–Z letter a key stands for (`é` is `E`), or null for anything else. */
export function letterOf(key: string): string | null {
  if ([...key].length !== 1) return null;
  const letter = normalizeWord(key);
  return /^[A-Z]$/.test(letter) ? letter : null;
}

export function emptyDefinition(): WordleDefinition {
  return {
    puzzleType: "wordle",
    typeVersion: 1,
    language: "en",
    length: DEFAULT_LENGTH,
    rulesText: {
      en: "Find the hidden word. Each guess must be a word of the right length from the dictionary. Every letter is then marked: in the right spot, in the word but elsewhere, or not in the word. A letter repeated in a guess is marked as many times as the hidden word holds it. Guesses are unlimited: only the time counts.",
      fr: "Trouvez le mot caché. Chaque proposition doit être un mot du dictionnaire de la bonne longueur. Chaque lettre est alors marquée : bien placée, présente ailleurs dans le mot, ou absente. Une lettre répétée n'est marquée qu'autant de fois que le mot caché la contient. Les accents ne comptent pas. Propositions illimitées : seul le temps compte.",
    },
  };
}

export function emptyProgress(_definition: WordleDefinition): WordleProgress {
  return { version: 1, draft: "", guesses: [] };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isMark(value: unknown): value is Mark {
  return typeof value === "string" && (MARKS as readonly string[]).includes(value);
}

function asGuess(value: unknown): Guess | null {
  if (!isRecord(value) || typeof value.word !== "string" || !Array.isArray(value.marks)) {
    return null;
  }
  const word = normalizeWord(value.word);
  if (!/^[A-Z]+$/.test(word) || value.marks.length !== word.length) return null;
  if (!value.marks.every(isMark)) return null;
  return { word, marks: [...value.marks] };
}

/** Accepts any stored blob (older versions, null, garbage) and returns a valid progress. */
export function migrateProgress(definition: WordleDefinition, raw: unknown): WordleProgress {
  if (!isRecord(raw)) return emptyProgress(definition);
  const guesses = Array.isArray(raw.guesses)
    ? raw.guesses.map(asGuess).filter((g): g is Guess => g !== null)
    : [];
  const unknown = Array.isArray(raw.unknown)
    ? raw.unknown.filter((w): w is string => typeof w === "string")
    : [];
  const draft =
    typeof raw.draft === "string"
      ? [...raw.draft]
          .map(letterOf)
          .filter((l): l is string => l !== null)
          .join("")
          .slice(0, lengthOf(definition))
      : "";
  return { version: 1, draft, guesses, ...(unknown.length > 0 ? { unknown } : {}) };
}

export function serializeSubmission(
  _definition: WordleDefinition,
  progress: WordleProgress,
): WordleSubmission {
  return { word: progress.draft };
}

export function isSolved(progress: WordleProgress): boolean {
  return progress.guesses.some((guess) => guess.marks.every((m) => m === "correct"));
}

function marksOf(feedback: Feedback[]): { word: string; marks: Mark[] } | null {
  const item = feedback.find((f) => f.code === "marks");
  const params = item?.params ?? {};
  if (!item || typeof params.word !== "string" || !Array.isArray(params.marks)) return null;
  if (!params.marks.every(isMark)) return null;
  return { word: normalizeWord(params.word), marks: [...params.marks] };
}

/**
 * Folds one verdict into the progress. A marked guess joins the history (once), the found
 * word joins it with every letter correct, an unknown word is listed as such and the draft is
 * kept for the player to fix. Anything else (an empty guess, a wrong length) changes nothing.
 */
export function applyVerdict(
  _definition: WordleDefinition,
  progress: WordleProgress,
  verdict: Verdict<WordleSubmission>,
): WordleProgress {
  const word = normalizeWord(verdict.submission.word);
  if (!word) return progress;
  const known = (w: string) => progress.guesses.some((guess) => guess.word === w);
  if (verdict.correct) {
    if (known(word)) return { ...progress, draft: "" };
    const marks: Mark[] = [...word].map(() => "correct");
    return { ...progress, draft: "", guesses: [...progress.guesses, { word, marks }] };
  }
  const marked = marksOf(verdict.feedback);
  if (marked) {
    if (known(marked.word)) return { ...progress, draft: "" };
    return { ...progress, draft: "", guesses: [...progress.guesses, marked] };
  }
  if (verdict.feedback.some((item) => item.code === "unknown_word")) {
    const unknown = progress.unknown ?? [];
    return { ...progress, unknown: unknown.includes(word) ? unknown : [...unknown, word] };
  }
  return progress;
}

const RANK: Record<Mark, number> = { absent: 0, present: 1, correct: 2 };

/** What the guesses taught about each letter: the best mark it ever received. */
export function letterStates(guesses: Guess[]): Map<string, Mark> {
  const states = new Map<string, Mark>();
  for (const guess of guesses) {
    [...guess.word].forEach((letter, i) => {
      const mark = guess.marks[i];
      const known = states.get(letter);
      if (mark && (known === undefined || RANK[mark] > RANK[known])) states.set(letter, mark);
    });
  }
  return states;
}

/** On-screen keyboard rows: the layout the player's physical keyboard most likely has. */
export const KEYBOARD_ROWS: Record<WordleDefinition["language"], string[]> = {
  en: ["QWERTYUIOP", "ASDFGHJKL", "ZXCVBNM"],
  fr: ["AZERTYUIOP", "QSDFGHJKLM", "WXCVBN"],
};
